import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import defaultTokens from "./defaultTokens";
import { applyTokensToDocument, deepMergeTokens } from "./utils";
import themeService from "../api/themeService";

const defaultThemeState = {
    id: null,
    label: defaultTokens.label,
    preset: defaultTokens.preset,
    tokens: defaultTokens,
    updatedAt: null,
    updatedBy: null,
};

const ThemeContext = createContext({
    theme: defaultThemeState,
    tokens: defaultTokens,
    status: "idle",
    error: null,
    previewTokens: null,
    previewTheme: () => { },
    clearPreview: () => { },
    refreshTheme: async () => { },
    saveTheme: async () => { },
});

const applyEffectiveTokens = (baseTokens, previewTokens) => {
    const effective = previewTokens
        ? deepMergeTokens(baseTokens, previewTokens)
        : baseTokens;
    applyTokensToDocument(effective);
};

export const ThemeProvider = ({ children }) => {
    const [state, setState] = useState({
        theme: defaultThemeState,
        status: "loading",
        error: null,
    });
    const [previewTokens, setPreviewTokens] = useState(null);
    const hasLoaded = useRef(false);

    const hydrateTheme = useCallback((themePayload) => {
        const mergedTokens = deepMergeTokens(
            defaultTokens,
            themePayload?.tokens || {}
        );

        setState({
            theme: {
                id: themePayload?.id || themePayload?._id || null,
                label: themePayload?.label || defaultTokens.label,
                preset: themePayload?.preset || defaultTokens.preset,
                tokens: mergedTokens,
                updatedAt: themePayload?.updatedAt || null,
                updatedBy: themePayload?.updatedBy || null,
            },
            status: "ready",
            error: null,
        });

        applyEffectiveTokens(mergedTokens, previewTokens);
    }, [previewTokens]);

    const loadTheme = useCallback(async () => {
        setState((prev) => ({ ...prev, status: "loading", error: null }));
        try {
            const response = await themeService.getTheme();
            hydrateTheme(response);
            hasLoaded.current = true;
        } catch (error) {
            console.error("Failed to load theme", error);
            setState((prev) => ({
                ...prev,
                status: "error",
                error:
                    error?.response?.data?.message ||
                    "Unable to load theme settings. Using defaults.",
            }));
            applyEffectiveTokens(defaultTokens, previewTokens);
        }
    }, [hydrateTheme, previewTokens]);

    useEffect(() => {
        if (!hasLoaded.current) {
            loadTheme();
        }
    }, [loadTheme]);

    useEffect(() => {
        applyEffectiveTokens(state.theme.tokens, previewTokens);
    }, [state.theme.tokens, previewTokens]);

    const previewTheme = useCallback(
        (partialTokens) => {
            if (!partialTokens || typeof partialTokens !== "object") {
                return;
            }
            setPreviewTokens((prev) =>
                deepMergeTokens(prev || {}, partialTokens)
            );
        },
        []
    );

    const clearPreview = useCallback(() => {
        setPreviewTokens(null);
    }, []);

    const saveTheme = useCallback(
        async ({ label, preset, tokens, updatedBy, isAdmin }) => {
            setState((prev) => ({ ...prev, status: "saving", error: null }));
            try {
                const response = await themeService.updateTheme({
                    label,
                    preset,
                    tokens,
                    updatedBy,
                    isAdmin,
                });
                hydrateTheme(response);
                setPreviewTokens(null);
                return response;
            } catch (error) {
                console.error("Failed to save theme", error);
                setState((prev) => ({
                    ...prev,
                    status: "error",
                    error:
                        error?.response?.data?.message ||
                        "Unable to save theme settings. Please try again.",
                }));
                throw error;
            }
        },
        [hydrateTheme]
    );

    const contextValue = useMemo(
        () => ({
            theme: state.theme,
            tokens: previewTokens || state.theme.tokens,
            status: state.status,
            error: state.error,
            previewTokens,
            previewTheme,
            clearPreview,
            refreshTheme: loadTheme,
            saveTheme,
        }),
        [
            state.theme,
            state.status,
            state.error,
            previewTokens,
            previewTheme,
            clearPreview,
            loadTheme,
            saveTheme,
        ]
    );

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useThemeContext = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error("useThemeContext must be used within a ThemeProvider");
    }
    return context;
};

export default ThemeContext;

